import React, { useState, useRef } from 'react';
import { Button } from '../ui';
import closeIcon from '../../assets/images/close.png';
import filesIcon from '../../assets/images/files.png';
import { useThemeContext } from '../../contexts/ThemeContext';

interface ImportRecordModalProps {
  onClose: () => void;
  onImport: (jsonData: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

const ImportRecordModal: React.FC<ImportRecordModalProps> = ({ 
  onClose, 
  onImport, 
  isLoading 
}) => {
  const { isDark } = useThemeContext();
  const [jsonData, setJsonData] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        // Validate JSON
        JSON.parse(content);
        setJsonData(content);
      } catch (err) {
        setError('Invalid JSON file');
        setFileName('');
      }
    };
    reader.readAsText(file);
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setJsonData(value);
    
    // Clear error when user types
    if (error) {
      setError('');
    }
  };

  const validateForm = (): boolean => {
    if (!jsonData.trim()) {
      setError('Please provide JSON data or select a file');
      return false;
    }

    try {
      JSON.parse(jsonData);
      return true;
    } catch (err) {
      setError('Invalid JSON format');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await onImport(jsonData);

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Failed to import record');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.json')) {
      setError('Please drop a JSON file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        JSON.parse(content);
        setJsonData(content);
      } catch (err) {
        setError('Invalid JSON file');
        setFileName('');
      }
    };
    reader.readAsText(file);
  };

  const getIcon = (lightIcon: string, darkIcon: string) => {
    return isDark ? darkIcon : lightIcon;
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-700 bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-light rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-grey_light">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-dark">Import Record</h3>
              <p className="text-grey_dark mt-1">Import a record from JSON file or paste JSON data</p>
            </div>
            <button
              onClick={onClose}
              className="text-grey_dark hover:text-dark p-2"
              aria-label="Close"
              disabled={isLoading}
            >
              <img
                src={closeIcon}
                alt="Close"
                className="w-6 h-6"
              />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload Section */}
            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                Upload JSON File
              </label>
              <div
                className="border-2 border-dashed border-grey_light rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".json"
                  className="hidden"
                  disabled={isLoading}
                />
                <img
                  src={filesIcon}
                  alt="Upload"
                  className="w-12 h-12 mx-auto mb-3 opacity-50"
                />
                <p className="text-grey_dark mb-1">
                  {fileName ? fileName : 'Drag & drop JSON file or click to browse'}
                </p>
                <p className="text-xs text-grey_dark">
                  Maximum file size: 5MB
                </p>
              </div>
            </div>

            {/* OR Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-grey_light"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-light text-grey_dark">OR</span>
              </div>
            </div>

            {/* JSON Text Area */}
            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                Paste JSON Data
              </label>
              <textarea
                value={jsonData}
                onChange={handleTextAreaChange}
                placeholder="Paste your JSON data here..."
                className="w-full h-48 p-3 border border-grey_light rounded-lg bg-light text-dark font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
                spellCheck="false"
              />
              {error && (
                <p className="text-danger text-sm mt-1">{error}</p>
              )}
            </div>

            {/* Instructions */}
            <div className="border-t border-grey_light pt-4">
              <div className="bg-bg_grey_light p-3 rounded-lg">
                <h4 className="font-medium text-dark mb-2">Import Instructions</h4>
                <ul className="text-sm text-grey_dark space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Import a record exported from another Balancio Pro instance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>JSON file must contain valid record data in Balancio Pro format</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>After import, the record will appear in your records list</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>You will be the owner of the imported record</span>
                  </li>
                </ul>
              </div>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-grey_light bg-bg_grey_light">
          <div className="flex justify-end space-x-3">
            <Button
              onClick={onClose}
              variant="bordered-primary"
              className="px-4 py-2"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="filled-primary"
              className="px-4 py-2"
              disabled={isLoading || !jsonData.trim()}
            >
              {isLoading ? 'Importing...' : 'Import Record'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportRecordModal;